"use client";

import { useEffect } from "react";

export default function Error({
    error,
    reset,
}: {
    error: Error & { digest?: string };
    reset: () => void;
}) {
    useEffect(() => {
        console.error("Application error:", error);
    }, [error]);

    return (
        <div
            style={{
                margin: 0,
                minHeight: "100vh",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                background: "linear-gradient(135deg, #FFEDF6, #DFD9FF)",
                fontFamily: "'Quicksand', sans-serif",
                overflow: "hidden",
                position: "relative",
            }}
        >
            {/* eslint-disable-next-line @next/next/no-page-custom-font */}
            <link
                href="https://fonts.googleapis.com/css2?family=Quicksand:wght@500;700&display=swap"
                rel="stylesheet"
            />

            {/* Background clouds */}
            <div
                style={{
                    position: "absolute",
                    background: "rgba(255,255,255,0.4)",
                    borderRadius: 100,
                    filter: "blur(25px)",
                    width: 400,
                    height: 150,
                    top: -50,
                    left: -150,
                    zIndex: 1,
                }}
            />
            <div
                style={{
                    position: "absolute",
                    background: "rgba(255,255,255,0.4)",
                    borderRadius: 100,
                    filter: "blur(25px)",
                    width: 500,
                    height: 200,
                    bottom: -80,
                    right: -200,
                    zIndex: 1,
                }}
            />

            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    textAlign: "center",
                    position: "relative",
                    zIndex: 10,
                }}
            >
                {/* Scene */}
                <div
                    style={{
                        position: "relative",
                        width: 300,
                        height: 280,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                    }}
                >
                    {/* Sparkles */}
                    {[
                        { top: 30, left: 20, size: 35, delay: "0s", dur: "3s" },
                        { top: 80, right: 10, size: 25, delay: "1s", dur: "2.5s" },
                        { top: 10, right: 70, size: 15, delay: "0.5s", dur: "4s" },
                        { bottom: 50, left: 0, size: 20, delay: "1.5s", dur: "2s" },
                    ].map((s, i) => (
                        <div
                            key={i}
                            style={{
                                position: "absolute",
                                background: "#FFFFFF",
                                clipPath:
                                    "polygon(50% 0%, 60% 40%, 100% 50%, 60% 60%, 50% 100%, 40% 60%, 0% 50%, 40% 40%)",
                                width: s.size,
                                height: s.size,
                                top: s.top,
                                left: s.left,
                                right: s.right,
                                bottom: s.bottom,
                                animation: `ghost-twinkle ${s.dur} ease-in-out infinite ${s.delay}`,
                            }}
                        />
                    ))}

                    {/* Floor shadow */}
                    <div
                        style={{
                            position: "absolute",
                            bottom: -30,
                            left: 95,
                            width: 110,
                            height: 15,
                            background: "rgba(140, 120, 179, 0.25)",
                            borderRadius: "50%",
                            filter: "blur(4px)",
                            animation: "ghost-shadow-pulse 4s ease-in-out infinite",
                        }}
                    />

                    {/* Ghost */}
                    <div style={{ position: "relative", animation: "ghost-float 4s ease-in-out infinite", zIndex: 5 }}>
                        <div
                            style={{
                                position: "relative",
                                width: 150,
                                height: 170,
                                background: "#FFFFFF",
                                borderRadius: "75px 75px 0 0",
                                boxShadow: "inset -10px -10px 20px rgba(0,0,0,0.03), 0 10px 30px rgba(140, 120, 179, 0.15)",
                            }}
                        >
                            {/* Arms */}
                            <div
                                style={{
                                    position: "absolute",
                                    top: 90,
                                    left: -15,
                                    width: 45,
                                    height: 35,
                                    background: "#FFFFFF",
                                    borderRadius: 20,
                                    zIndex: -1,
                                    boxShadow: "inset 5px -5px 10px rgba(0,0,0,0.03)",
                                    animation: "ghost-wave-left 4s ease-in-out infinite",
                                    transformOrigin: "right center",
                                }}
                            />
                            <div
                                style={{
                                    position: "absolute",
                                    top: 90,
                                    right: -15,
                                    width: 45,
                                    height: 35,
                                    background: "#FFFFFF",
                                    borderRadius: 20,
                                    zIndex: -1,
                                    boxShadow: "inset -5px -5px 10px rgba(0,0,0,0.03)",
                                    animation: "ghost-wave-right 4s ease-in-out infinite",
                                    transformOrigin: "left center",
                                }}
                            />

                            {/* Face */}
                            <div style={{ position: "absolute", top: 60, left: 0, width: "100%", height: 60 }}>
                                {/* Eyes - X'd out for error */}
                                <div
                                    style={{
                                        position: "absolute",
                                        top: 2,
                                        left: 42,
                                        fontSize: 20,
                                        fontWeight: 700,
                                        color: "#3B3349",
                                        lineHeight: 1,
                                    }}
                                >
                                    ✕
                                </div>
                                <div
                                    style={{
                                        position: "absolute",
                                        top: 2,
                                        right: 42,
                                        fontSize: 20,
                                        fontWeight: 700,
                                        color: "#3B3349",
                                        lineHeight: 1,
                                    }}
                                >
                                    ✕
                                </div>
                                {/* Blush */}
                                <div
                                    style={{
                                        position: "absolute",
                                        top: 15,
                                        left: 20,
                                        width: 24,
                                        height: 12,
                                        background: "#FFB3C6",
                                        borderRadius: "50%",
                                        filter: "blur(3px)",
                                        opacity: 0.8,
                                    }}
                                />
                                <div
                                    style={{
                                        position: "absolute",
                                        top: 15,
                                        right: 20,
                                        width: 24,
                                        height: 12,
                                        background: "#FFB3C6",
                                        borderRadius: "50%",
                                        filter: "blur(3px)",
                                        opacity: 0.8,
                                    }}
                                />
                                {/* Wobbly mouth */}
                                <div
                                    style={{
                                        position: "absolute",
                                        top: 22,
                                        left: 62,
                                        fontSize: 18,
                                        color: "#3B3349",
                                        lineHeight: 1,
                                    }}
                                >
                                    ~
                                </div>
                            </div>

                            {/* Scalloped skirt */}
                            <div
                                style={{
                                    position: "absolute",
                                    bottom: -15,
                                    left: 0,
                                    width: "100%",
                                    display: "flex",
                                }}
                            >
                                {[0, 1, 2, 3].map((i) => (
                                    <div
                                        key={i}
                                        style={{
                                            flex: 1,
                                            height: 30,
                                            background: "#FFFFFF",
                                            borderRadius: "0 0 50% 50%",
                                            boxShadow: "inset 0 -4px 8px rgba(0,0,0,0.02)",
                                        }}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Text */}
                <div style={{ marginTop: 10 }}>
                    <h1
                        style={{
                            fontSize: "3.4rem",
                            color: "#4A3E5C",
                            margin: 0,
                            letterSpacing: -2,
                            textShadow: "2px 4px 10px rgba(140,120,179,0.2)",
                            fontFamily: "'Quicksand', sans-serif",
                        }}
                    >
                        Oops!
                    </h1>
                    <h2
                        style={{
                            fontSize: "1.4rem",
                            color: "#5D4F70",
                            margin: "5px 0 10px 0",
                            fontFamily: "'Quicksand', sans-serif",
                        }}
                    >
                        Something went wrong
                    </h2>
                    <p
                        style={{
                            fontSize: "1rem",
                            color: "#8C78B3",
                            margin: "0 0 30px 0",
                            fontWeight: 500,
                            fontFamily: "'Quicksand', sans-serif",
                            maxWidth: 360,
                        }}
                    >
                        Our little ghost tripped over a wire. Try again?
                    </p>
                    <button
                        onClick={reset}
                        style={{
                            background: "#FFFFFF",
                            color: "#8C78B3",
                            border: "none",
                            padding: "14px 38px",
                            borderRadius: 30,
                            fontSize: "1.1rem",
                            fontFamily: "'Quicksand', sans-serif",
                            fontWeight: 700,
                            cursor: "pointer",
                            boxShadow: "0 8px 20px rgba(140, 120, 179, 0.15)",
                            transition: "all 0.25s ease",
                        }}
                    >
                        Try again
                    </button>
                </div>
            </div>

            <style>{`
                @keyframes ghost-float {
                    0%, 100% { transform: translateY(0); }
                    50% { transform: translateY(-15px); }
                }
                @keyframes ghost-shadow-pulse {
                    0%, 100% { transform: scale(1); opacity: 0.8; }
                    50% { transform: scale(0.85); opacity: 0.4; }
                }
                @keyframes ghost-wave-left {
                    0%, 100% { transform: rotate(-35deg); }
                    50% { transform: rotate(-15deg); }
                }
                @keyframes ghost-wave-right {
                    0%, 100% { transform: rotate(35deg); }
                    50% { transform: rotate(15deg); }
                }
                @keyframes ghost-twinkle {
                    0%, 100% { opacity: 0; transform: scale(0.5) rotate(0deg); }
                    50% { opacity: 1; transform: scale(1) rotate(45deg); }
                }
            `}</style>
        </div>
    );
}
